import { useState, type FormEvent } from 'react';
import { motion } from 'framer-motion';
import { Loader2, AlertCircle } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import { useBranding } from '@/lib/use-cobrand';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Clean, professional split login. Left: a solid brand panel (no gradients,
// no decorative blobs) with the tenant's logo + name. Right: a calm, focused
// sign-in form. Everything reads from the co-brand CSS vars, so it's correct
// for any tenant color and for the default RefDash skin.
export function LoginPage() {
  const { login } = useAuth();
  const brand = useBranding();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      await login(username, password);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Invalid credentials');
    } finally {
      setIsLoading(false);
    }
  }

  const logo = brand.logo_url ?? `${import.meta.env.BASE_URL}logo.svg`;

  return (
    <div className="min-h-dvh flex">
      {/* ── Brand panel (desktop) ─────────────────────────────── */}
      <div className="relative hidden w-[42%] flex-col justify-between bg-primary p-14 text-white lg:flex">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white p-2 shadow-sm ring-1 ring-black/5">
          <img src={logo} alt={brand.display_name} className="h-full w-full object-contain" />
        </div>

        <div>
          <h1 className="font-sans text-4xl font-bold leading-tight tracking-tight text-balance">
            {brand.display_name}
          </h1>
          <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-white/85">
            Review incoming referrals, decide with your team, and act — all in one secure workspace.
          </p>
        </div>

        <p className="text-sm text-white/70">Powered by RefDash</p>
      </div>

      {/* ── Sign-in form ──────────────────────────────────────── */}
      <div className="flex flex-1 items-center justify-center bg-background px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          {/* Mobile brand */}
          <div className="mb-8 flex flex-col items-center text-center lg:hidden">
            <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-white p-2.5 shadow-sm ring-1 ring-border">
              <img src={logo} alt={brand.display_name} className="h-full w-full object-contain" />
            </div>
            <h1 className="font-sans text-xl font-bold tracking-tight text-foreground text-balance">
              {brand.display_name}
            </h1>
          </div>

          {/* Desktop heading */}
          <div className="mb-8 hidden lg:block">
            <h2 className="font-sans text-2xl font-bold tracking-tight text-foreground">Welcome back</h2>
            <p className="mt-1.5 text-sm text-muted-foreground">Sign in to continue to {brand.display_name}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-start gap-2.5 rounded-xl border border-red-100 bg-red-50 p-3.5 text-sm text-red-700">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div className="space-y-1.5">
              <label htmlFor="username" className="text-sm font-semibold text-foreground">Username</label>
              <Input
                id="username" type="text" autoComplete="username" autoCapitalize="off"
                value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="Enter your username" required disabled={isLoading}
              />
            </div>

            <div className="space-y-1.5">
              <label htmlFor="password" className="text-sm font-semibold text-foreground">Password</label>
              <Input
                id="password" type="password" autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password" required disabled={isLoading}
              />
            </div>

            <Button type="submit" size="lg" className="mt-2 w-full" disabled={isLoading || !username || !password}>
              {isLoading ? (<><Loader2 className="h-5 w-5 animate-spin" /> Signing in…</>) : 'Sign in'}
            </Button>
          </form>

          <p className="mt-10 text-center text-xs text-muted-foreground">
            Secure access for authorized representatives
            <span className="lg:hidden"> · Powered by RefDash</span>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
